import React, { useState, useEffect } from "react"
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/card"
import { RadioGroup, RadioGroupItem } from "../components/radio-group"
import { Label } from "../components/label"
import { Button } from "../components/button"
import { Progress } from "../components/progress"
import { Sparkles, Award, TrendingUp, Users, Lightbulb, ArrowRight, FileText, Download, AlertCircle, CheckCircle2, BookOpen, ChevronLeft, ChevronRight, Check, AlertTriangle, Minus, X, BarChart3, Zap, Shield, RefreshCw, Calendar, Heart, Clock, Search, Brain, MessageSquare, Crown } from 'lucide-react'
import TestResultUI from "../components/TestResultUI"
import Navbar from "../components/Navbar"
import Sidebar from "../components/Sidebar"
import api from "../sever/Api";
import { notifyTestCompletion } from "../lib/testUtils";

const sections = [
  {
    title: "Leading & Deciding",
    shortTitle: "Leading",
    icon: Crown,
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50",
    iconColor: "text-purple-600",
    description: "Leadership, decision-making, taking charge",
    questions: [
      {
        text: "When working in a group project, I prefer to:",
        options: ["Take charge and guide the team", "Contribute ideas but let others lead", "Follow instructions and complete tasks", "Stay neutral and avoid responsibility"]
      },
      {
        text: "A good leader should primarily:",
        options: ["Inspire and motivate others", "Focus on getting results", "Maintain harmony in the team", "Follow established rules strictly"]
      },
      {
        text: "If there is a conflict in the team, I usually:",
        options: ["Step in and make a decision quickly", "Try to listen to both sides first", "Avoid getting involved", "Wait for someone else to handle it"]
      },
      {
        text: "I feel most comfortable when:",
        options: ["I am in control of decisions", "I'm supporting others' goals", "I'm doing my own work independently", "I'm following clear instructions"]
      },
      {
        text: "In a competitive environment, I usually:",
        options: ["Take initiative and try to win", "Support the group to succeed together", "Stay calm and do my part quietly", "Avoid competing too strongly"]
      },
      {
        text: "When given authority, I:",
        options: ["Use it to make clear rules", "Try to balance fairness and results", "Share responsibilities with others", "Prefer not to take full control"]
      },
      {
        text: "A strong decision-maker should:",
        options: ["Be confident and quick", "Consider everyone's input", "Avoid risks and play safe", "Follow the leader's example"]
      },
      {
        text: "I enjoy tasks where I:",
        options: ["Take leadership and responsibility", "Support others to achieve success", "Work steadily without pressure", "Follow structured rules"]
      },
      {
        text: "When rules are unclear, I:",
        options: ["Set my own rules for others", "Ask for clarification from peers", "Wait for someone else to decide", "Stick to what has been done before"]
      },
      {
        text: "My leadership style is closest to:",
        options: ["Direct and decisive", "Supportive and inclusive", "Careful and observant", "Strict and rule-focused"]
      },
      {
        text: "I feel uncomfortable when:",
        options: ["I cannot make decisions", "I cannot help others", "I'm under pressure", "I must break the rules"]
      },
      {
        text: "When managing a project, my focus is:",
        options: ["Achieving results quickly", "Keeping the team motivated", "Avoiding mistakes", "Ensuring rules are followed"]
      },
      {
        text: "Others usually see me as:",
        options: ["Assertive and bold", "Friendly and cooperative", "Quiet and reliable", "Serious and disciplined"]
      },
      {
        text: "For me, the most important quality in a leader is:",
        options: ["Confidence and vision", "Empathy and fairness", "Calmness and patience", "Discipline and order"]
      }
    ],
  },
  {
    title: "Supporting & Cooperating",
    shortTitle: "Supporting",
    icon: Heart,
    color: "from-pink-500 to-rose-500",
    bgColor: "bg-pink-50",
    iconColor: "text-pink-600",
    description: "Teamwork, cooperation, supporting others",
    questions: [
      {
        text: "When working with others, I usually:",
        options: ["Try to help and support them", "Focus mainly on my own tasks", "Motivate the team with encouragement", "Wait for guidance before acting"]
      },
      {
        text: "If a colleague is struggling, I:",
        options: ["Offer my help immediately", "Suggest resources they could use", "Wait until they ask for help", "Focus on finishing my work first"]
      },
      {
        text: "The most important value in teamwork is:",
        options: ["Cooperation", "Competition", "Independence", "Obedience"]
      },
      {
        text: "I prefer to work in environments where:",
        options: ["People share and help each other", "Everyone works on their own tasks", "There's healthy competition", "Rules guide interactions"]
      },
      {
        text: "If someone disagrees with me, I:",
        options: ["Try to understand their perspective", "Stick firmly to my opinion", "Avoid the conflict", "Let them decide"]
      },
      {
        text: "A supportive colleague is someone who:",
        options: ["Listens and helps when needed", "Works independently without disturbing others", "Avoids conflicts at all costs", "Follows rules and procedures"]
      },
      {
        text: "I feel best in a team when:",
        options: ["Everyone works together", "I can contribute individually", "There is no conflict", "Everyone knows the rules"]
      },
      {
        text: "If a new team member joins, I:",
        options: ["Welcome and guide them", "Let them find their own way", "Stay neutral until they adjust", "Wait for the leader to help them"]
      },
      {
        text: "I am most satisfied at work when:",
        options: ["I help others succeed", "I achieve personal goals", "I avoid mistakes", "I follow clear guidelines"]
      },
      {
        text: "Team harmony is best achieved by:",
        options: ["Listening and supporting each other", "Working silently and independently", "Avoiding arguments", "Following leader instructions"]
      },
      {
        text: "In group discussions, I:",
        options: ["Encourage quieter members to speak", "Focus on my own contributions", "Remain quiet to avoid conflict", "Follow what the leader says"]
      },
      {
        text: "I believe cooperation is about:",
        options: ["Sharing responsibilities", "Respecting differences", "Avoiding disagreements", "Following team rules"]
      },
      {
        text: "When colleagues achieve success, I:",
        options: ["Celebrate with them", "Focus on my own progress", "Stay quiet", "Respect the process"]
      },
      {
        text: "The most important trait of a supportive person is:",
        options: ["Empathy", "Independence", "Patience", "Discipline"]
      }
    ],
  },
  {
    title: "Interacting & Presenting",
    shortTitle: "Interacting",
    icon: MessageSquare,
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-50",
    iconColor: "text-blue-600",
    description: "Communication, presentation, social interaction",
    questions: [
      {
        text: "When giving a presentation, I:",
        options: ["Feel confident and enjoy it", "Get nervous but manage", "Prefer not to present", "Only present if I must"]
      },
      {
        text: "I am most comfortable communicating:",
        options: ["Face to face with people", "Through written messages", "Only when necessary", "By following a script"]
      },
      {
        text: "In group meetings, I usually:",
        options: ["Speak confidently and share ideas", "Contribute only when asked", "Prefer to listen more than talk", "Stick to the agenda strictly"]
      },
      {
        text: "I feel energized when:",
        options: ["Talking to new people", "Working quietly alone", "Avoiding public attention", "Following structured communication"]
      },
      {
        text: "A good presenter should:",
        options: ["Be engaging and clear", "Stick to facts only", "Keep things short", "Follow the slides exactly"]
      },
      {
        text: "When meeting strangers, I:",
        options: ["Enjoy starting conversations", "Wait for them to speak first", "Stay quiet and observe", "Follow polite formalities"]
      },
      {
        text: "I express myself best through:",
        options: ["Speaking", "Writing", "Silence", "Following rules"]
      },
      {
        text: "If my idea is challenged, I:",
        options: ["Defend it confidently", "Listen and respond carefully", "Stay quiet", "Agree to avoid conflict"]
      },
      {
        text: "I enjoy roles where I:",
        options: ["Interact and network with people", "Work independently without interruptions", "Avoid attention", "Follow formal communication protocols"]
      },
      {
        text: "I feel uncomfortable when:",
        options: ["I cannot express my ideas", "I must speak too much", "I am the center of attention", "I must improvise"]
      },
      {
        text: "When leading a discussion, I:",
        options: ["Engage others and ask questions", "Stick to the main topic", "Let others dominate", "Follow prepared notes only"]
      },
      {
        text: "People describe my communication as:",
        options: ["Open and friendly", "Calm and to the point", "Quiet and reserved", "Formal and serious"]
      },
      {
        text: "For me, effective communication means:",
        options: ["Clear understanding between people", "Getting the facts right", "Avoiding misunderstandings", "Following official standards"]
      },
      {
        text: "My favorite way of presenting is:",
        options: ["With stories and energy", "With facts and data", "With short notes", "With structured slides"]
      }
    ],
  },
  {
    title: "Analyzing & Interpreting",
    shortTitle: "Analyzing",
    icon: Brain,
    color: "from-emerald-500 to-green-500",
    bgColor: "bg-emerald-50",
    iconColor: "text-emerald-600",
    description: "Problem-solving, analysis, logical thinking",
    questions: [
      {
        text: "I enjoy tasks where I:",
        options: ["Solve problems logically", "Support others emotionally", "Avoid complex thinking", "Follow ready-made solutions"]
      },
      {
        text: "I prefer working on projects that:",
        options: ["Involve research and analysis", "Involve teamwork and cooperation", "Are simple and repetitive", "Have clear instructions"]
      },
      {
        text: "When faced with a problem, I:",
        options: ["Break it into smaller parts", "Ask others for help", "Wait for someone else to solve it", "Use standard procedures"]
      },
      {
        text: "The best decisions are made by:",
        options: ["Careful analysis", "Considering emotions", "Avoiding overthinking", "Following rules"]
      },
      {
        text: "I feel most satisfied when:",
        options: ["I find a logical solution", "I help someone in need", "I complete simple tasks", "I follow set procedures"]
      },
      {
        text: "My strength lies in:",
        options: ["Problem-solving", "Supporting people", "Following directions", "Maintaining order"]
      },
      {
        text: "When analyzing data, I:",
        options: ["Look for patterns and insights", "Ask others for their opinion", "Avoid too much detail", "Stick to official formats"]
      },
      {
        text: "I enjoy activities that:",
        options: ["Challenge my brain", "Involve teamwork", "Don't require thinking deeply", "Have clear instructions"]
      },
      {
        text: "I believe intelligence is best shown by:",
        options: ["Solving problems effectively", "Supporting others", "Staying calm", "Following rules"]
      },
      {
        text: "When reading complex information, I:",
        options: ["Break it into steps", "Ask others to explain", "Skip difficult parts", "Look for summaries"]
      },
      {
        text: "I prefer solving problems by:",
        options: ["Logic and reasoning", "Emotional understanding", "Avoiding risks", "Following rules"]
      },
      {
        text: "My colleagues would describe me as:",
        options: ["Analytical", "Supportive", "Quiet", "Disciplined"]
      },
      {
        text: "The most rewarding part of problem-solving is:",
        options: ["Finding the right answer", "Helping others understand", "Avoiding mistakes", "Following procedure"]
      },
      {
        text: "For me, accuracy means:",
        options: ["Using logic and facts", "Making others happy", "Staying safe", "Following rules correctly"]
      }
    ],
  },
  {
    title: "Creating & Conceptualizing",
    shortTitle: "Creating",
    icon: Lightbulb,
    color: "from-yellow-500 to-orange-500",
    bgColor: "bg-yellow-50",
    iconColor: "text-yellow-600",
    description: "Creativity, innovation, original thinking",
    questions: [
      {
        text: "I feel excited when:",
        options: ["Thinking of new ideas", "Supporting others' goals", "Doing routine work", "Following strict guidelines"]
      },
      {
        text: "Creativity means:",
        options: ["Coming up with original solutions", "Making others happy", "Doing tasks carefully", "Following instructions"]
      },
      {
        text: "I enjoy tasks where I:",
        options: ["Imagine new possibilities", "Support a team", "Do simple steps repeatedly", "Follow a manual"]
      },
      {
        text: "When facing a challenge, I:",
        options: ["Think of new ways to solve it", "Ask others for support", "Avoid risk", "Stick to proven methods"]
      },
      {
        text: "The best innovations come from:",
        options: ["Thinking outside the box", "Working together as a team", "Staying consistent", "Following established rules"]
      },
      {
        text: "My strength is:",
        options: ["Creativity", "Cooperation", "Discipline", "Obedience"]
      },
      {
        text: "I am most motivated when:",
        options: ["I can be original", "I can support others", "I have a clear routine", "I have rules to follow"]
      },
      {
        text: "I prefer to work in environments that:",
        options: ["Encourage new ideas", "Focus on teamwork", "Avoid risks", "Have strong rules"]
      },
      {
        text: "When I have a new idea, I:",
        options: ["Share it confidently", "Check if others agree", "Keep it to myself", "Compare it with rules"]
      },
      {
        text: "The most creative people are:",
        options: ["Open-minded and bold", "Caring and helpful", "Careful and steady", "Disciplined and structured"]
      },
      {
        text: "My colleagues would say I am:",
        options: ["Innovative", "Supportive", "Careful", "Rule-following"]
      },
      {
        text: "For me, creativity is most useful when:",
        options: ["Solving problems differently", "Making people happy", "Avoiding mistakes", "Following procedures better"]
      },
      {
        text: "I enjoy activities that:",
        options: ["Let me use imagination", "Involve teamwork", "Require discipline", "Have fixed rules"]
      },
      {
        text: "I feel proud when:",
        options: ["I create something new", "I help others succeed", "I avoid risks", "I follow the rules correctly"]
      }
    ],
  }
];

const options = [
  {
    value: "0",
    label: "Never applies",
    score: 1,
    icon: X,
    color: "text-red-600",
  },
  {
    value: "1",
    label: "Rarely applies",
    score: 2,
    icon: Minus,
    color: "text-orange-600",
  },
  {
    value: "2",
    label: "Sometimes applies",
    score: 3,
    icon: AlertTriangle,
    color: "text-yellow-600",
  },
  {
    value: "3",
    label: "Often applies",
    score: 4,
    icon: Check,
    color: "text-green-600",
  },
  {
    value: "4",
    label: "Always applies",
    score: 5,
    icon: Check,
    color: "text-green-600",
  },
]

export default function CompetencyQuiz() {
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState(0)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [responses, setResponses] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [resultsSaved, setResultsSaved] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showIntro, setShowIntro] = useState(true);
  const [autoNext, setAutoNext] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const totalQuestions = sections.reduce((sum, section) => sum + section.questions.length, 0)
  const answeredQuestions = Object.keys(responses).length
  const progress = (answeredQuestions / totalQuestions) * 100

  // Calculate progress for each section
  const getSectionProgress = (sectionIndex) => {
    const section = sections[sectionIndex]
    const answeredInSection = section.questions.filter((_, questionIndex) => {
      const key = `${sectionIndex}-${questionIndex}`
      return responses[key]
    }).length
    return {
      answered: answeredInSection,
      total: section.questions.length
    }
  }

  const handleResponse = (value) => {
    const key = `${currentSection}-${currentQuestion}`
    setResponses((prev) => ({
      ...prev,
      [key]: value,
    }))
    setAutoNext(true);
  }

  const getResponse = (sectionIndex, questionIndex) => {
    const key = `${sectionIndex}-${questionIndex}`
    return responses[key] || ""
  }

  const getCurrentResponse = () => {
    return getResponse(currentSection, currentQuestion)
  }

  const canProceed = () => {
    return getCurrentResponse() !== ""
  }

  const handleNext = () => {
    if (currentQuestion < sections[currentSection].questions.length - 1) {
      // Move to next question in current section
      setCurrentQuestion(currentQuestion + 1)
    } else if (currentSection < sections.length - 1) {
      // Move to first question of next section
      setCurrentSection(currentSection + 1)
      setCurrentQuestion(0)
    } else {
      // Quiz completed - notify test completion for real-time updates
      const testData = {
        scores: calculateResults(),
        answers: responses,
        overallScore: Math.round(Object.values(calculateResults()).reduce((sum, result) => sum + result.percentage, 0) / Object.keys(calculateResults()).length),
        testType: 'competency',
        completed: true
      };

      notifyTestCompletion('competency', testData);
      setShowResults(true)
    }
  }

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      // Move to previous question in current section
      setCurrentQuestion(currentQuestion - 1)
    } else if (currentSection > 0) {
      // Move to last question of previous section
      setCurrentSection(currentSection - 1)
      setCurrentQuestion(sections[currentSection - 1].questions.length - 1)
    }
  }

  const isFirstQuestion = currentSection === 0 && currentQuestion === 0
  const isLastQuestion = currentSection === sections.length - 1 && currentQuestion === sections[currentSection].questions.length - 1

  // Calculate current question number globally
  const getCurrentQuestionNumber = () => {
    let questionNumber = 1
    for (let i = 0; i < currentSection; i++) {
      questionNumber += sections[i].questions.length
    }
    questionNumber += currentQuestion
    return questionNumber
  }

  const calculateResults = () => {
    const sectionScores = sections.map((section, sectionIndex) => {
      const sectionTotal = section.questions.reduce((sum, _, questionIndex) => {
        const response = getResponse(sectionIndex, questionIndex)
        // Convert response (0,1,2,3) to score (4,3,2,1)
        const score = response ? (4 - parseInt(response)) : 0
        return sum + score
      }, 0)
      const maxScore = section.questions.length * 4
      const percentage = (sectionTotal / maxScore) * 100
      return {
        title: section.title,
        icon: section.icon,
        color: section.color,
        score: sectionTotal,
        maxScore,
        percentage: Math.round(percentage),
      }
    })
    return sectionScores
  }

  const saveTestResult = async () => {
    try {
      const results = calculateResults();
      const overallScore = results.reduce((sum, result) => sum + result.score, 0);
      const overallMaxScore = results.reduce((sum, result) => sum + result.maxScore, 0);
      const overallPercentage = Math.round((overallScore / overallMaxScore) * 100);

      const detailedAnswers = [];
      sections.forEach((section, sectionIndex) => {
        section.questions.forEach((question, questionIndex) => {
          const response = getResponse(sectionIndex, questionIndex);
          if (response) {
            detailedAnswers.push({
              questionId: `${sectionIndex}-${questionIndex}`,
              questionText: question.text,
              selectedOptionId: response,
              selectedOptionText:
                question.options[parseInt(response)] || "Not answered",
            });
          }
        });
      });

      const { data } = await api.post("/api/test-results", {
        testType: "competency",
        answers: detailedAnswers,
        scores: results,
        overallScore,
        overallPercentage,
      });

      toast.success("Test results saved successfully!");
    } catch (error) {
      console.error("Error saving test result:", error);
      toast.error(
        error.response?.data?.message || "Failed to save test results"
      );
    }
  };

  // Save results when they are first shown
  useEffect(() => {
    if (showResults) {
      saveTestResult();
    }
  }, [showResults]);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowResults(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (autoNext) {
      if (!isLastQuestion) {
        handleNext();
      }
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  const resetQuiz = () => {
    setShowResults(false)
    setCurrentSection(0)
    setCurrentQuestion(0)
    setResponses({})
  }

  if (showIntro) {
    const totalQuestions = sections.reduce(
      (sum, section) => sum + section.questions.length,
      0
    );

    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">
                    Competency Quiz
                  </CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Reflect on your skills, goals, and impact to discover
                    your core competencies
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-purple-600">Values</h3>
                      <p className="text-sm text-gray-600">
                        Explore your guiding principles & what matters most
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-indigo-600">Passions</h3>
                      <p className="text-sm text-gray-600">
                        Identify activities and interests that energize you
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-green-600">Skills</h3>
                      <p className="text-sm text-gray-600">
                        Evaluate strengths, talents, and competencies
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-pink-600">Impact</h3>
                      <p className="text-sm text-gray-600">
                        Recognize how you contribute to others & society
                      </p>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{totalQuestions} questions</strong> •{" "}
                      Gain insights into your
                      competencies
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      // Mark free test as used when user actually starts the test
                      try {
                        const userStr = localStorage.getItem('user');
                        const user = userStr ? JSON.parse(userStr) : null;
                        const isPremium = user && (user.role === 'premium' || user.role === 'admin');
                        if (!isPremium) {
                          localStorage.setItem('freeTestUsed', '1');
                        }
                      } catch (_) {}
                      setShowIntro(false);
                    }}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const results = calculateResults()
    const overallScore = results.reduce((sum, result) => sum + result.score, 0)
    const overallMaxScore = results.reduce((sum, result) => sum + result.maxScore, 0)
    const overallPercentage = Math.round((overallScore / overallMaxScore) * 100)

    const getLevel = (percentage) => {
      if (percentage >= 80) return { level: 'Very High', color: 'bg-red-500' }
      if (percentage >= 65) return { level: 'High', color: 'bg-orange-500' }
      if (percentage >= 50) return { level: 'Moderate', color: 'bg-yellow-500' }
      if (percentage >= 35) return { level: 'Low', color: 'bg-blue-500' }
      return { level: 'Very Low', color: 'bg-green-500' }
    }

    const resultsContent = (
      <>
        <div className="text-center">
          <div className="inline-block">
            <div className="text-6xl font-bold text-gray-900 mb-2">{overallPercentage}%</div>
            <div className="text-sm text-gray-600">Overall Competency Score</div>
          </div>
        </div>
        {results.map((result, index) => {
          const { level, color } = getLevel(result.percentage)
          const SectionIcon = result.icon
          return (
            <div key={index} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <SectionIcon className="w-5 h-5 text-gray-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900">{result.title}</h3>
                </div>
                <div className="text-right">
                  <span className="font-bold">{result.score}/{result.maxScore}</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs text-white ${color}`}>{level}</span>
                </div>
              </div>
              <Progress value={result.percentage} className="h-3" />
              <p className="text-sm text-gray-600">{sections[index].description}</p>
            </div>
          )
        })}
      </>
    )

    const detailedReportContent = (
      <>
        {results.map((result, index) => {
          const { level, color } = getLevel(result.percentage)
          const SectionIcon = result.icon
          return (
            <Card className='bg-[#ffffff]' key={index}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <SectionIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{result.title}</CardTitle>
                      <CardDescription>{sections[index].description}</CardDescription>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{result.score}/{result.maxScore}</div>
                    <span className={`px-3 py-1 rounded-full text-sm text-white ${color}`}>{level}</span>
                  </div>
                </div>
                <Progress value={result.percentage} className="h-2" />
              </CardHeader>
            </Card>
          )
        })}
      </>
    )

    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>
        <div className="ml-0 md:ml-64 min-h-screen">
          <Navbar toggleSidebar={toggleSidebar} />
          <TestResultUI
            testTitle="Competency Assessment"
            testDescription="Category-wise performance summary"
            results={resultsContent}
            aiReportData={{
              testType: 'competency',
              scores: results.reduce((acc, r) => {
                acc[r.title] = { score: r.score, maxScore: r.maxScore, percentage: r.percentage };
                return acc;
              }, {}),
              answers: sections.flatMap((section, sectionIndex) =>
                section.questions.map((q, questionIndex) => {
                  const response = getResponse(sectionIndex, questionIndex);
                  return response ? {
                    questionId: `${sectionIndex}-${questionIndex}`,
                    questionText: q.text,
                    selectedOptionId: response,
                    selectedOptionText: q.options[parseInt(response)] || 'Not answered'
                  } : null;
                }).filter(Boolean)
              ),
              overallScore: overallScore,
              overallPercentage: overallPercentage
            }}
            onRetake={resetQuiz}
            onBack={() => { }}
            showBackButton={false}
          >
            {detailedReportContent}
          </TestResultUI>
        </div>
      </div>
    )
  }

  const currentSectionData = sections[currentSection]
  const currentQuestionText = currentSectionData.questions[currentQuestion]
  const currentQuestionNumber = getCurrentQuestionNumber()
  const isAnswered = getCurrentResponse() !== ""


  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      {/* Sidebar */}
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen ">
        <Navbar
          toggleSidebar={toggleSidebar}
        />
        <div className="min-h-screen ">
          <div className=" mx-auto p-6" style={{ width: '85%' }}>
            <div className="text-center mb-8">
              {/* <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div> */}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Competency Quiz</h1>
              <p className="text-gray-600 max-w-2xl mx-auto">Set the tone and encourage honest introspection about core values, passions, goals, and impact. Acts as an engaging, personal entry point into the journey</p>
            </div>
            <div className="rounded-lg border text-card-foreground shadow-sm mb-6 bg-[#ffffff] p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Your Progress by Category</h2>

              {/* Category Icons */}
              <div className="grid grid-cols-5 gap-4 mb-8">
                {sections.map((section, index) => {
                  const progress = getSectionProgress(index)
                  const Icon = section.icon

                  return (
                    <div key={section.title} className="text-center">
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                        <Icon className={`w-8 h-8 ${section.iconColor}`} />
                      </div>
                      <h3 className="font-medium text-sm text-gray-900 mb-1">
                        {section.shortTitle}
                      </h3>
                      <div className="text-sm text-gray-500">
                        {progress.answered}/{progress.total}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">
                  Question {currentQuestionNumber} of {totalQuestions}
                </span>
                <span className="text-sm text-gray-600">
                  {Math.round(progress)}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            {/* Main Question Card */}
            <div className="rounded-lg border text-card-foreground shadow-sm bg-[#ffffff]">
              {/* Section Header */}
              <div className="border-b p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {currentSectionData.title}
                    </h1>
                    <p className="text-gray-600">
                      {currentSectionData.description}
                    </p>
                  </div>
                  {/* {!isAnswered && (
                    <span className="text-sm text-orange-600 font-medium">
                      Unanswered
                    </span>
                  )} */}
                </div>
              </div>

              {/* Question */}
              <div className="mb-8 p-5">
                <h2 className="text-lg font-medium text-gray-900 mb-6">
                  {currentSection + 1}.{currentQuestion + 1} {currentQuestionText.text}
                </h2>

                {/* Answer Options */}
                <div className="space-y-3">
                  {currentQuestionText.options.map((option, optionIndex) => {
                    const isSelected = getCurrentResponse() === optionIndex.toString();

                    return (
                      <div
                        key={optionIndex}
                        onClick={() => handleResponse(optionIndex.toString())}
                        className={`flex items-center gap-3 p-2 px-4 border rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                          ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                          : "hover:bg-[rgb(197,199,203)] dark:hover:bg-gray-800"
                          }`}
                      >
                        <span className="">
                          {option}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation */}
            </div>
            <div className="flex justify-between items-center mt-5">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={isFirstQuestion}
                className="text-gray-600 border-gray-300"
              >
                Previous
              </Button>

              {/* {!isLastQuestion && (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                >
                  Next Question
                </Button>
              )} */}
              {isLastQuestion && (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                >
                  View Results
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 